import {getTestProps} from '@benchling/js-test-utils';
import {
  ActionIcon,
  Alert,
  Badge,
  Group,
  Menu,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  createStyles,
} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {PREDICTION_JOB_RESULTS_DOWNLOAD_URL_QUERY} from 'model-hub/ModelHubQueries';
import {
  requireScientificModel,
  type ScientificModelNode,
  type ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

import GetInTouchButton from './contact-us/GetInTouchButton';
import type {SortDirection} from './lib/listingTypes';
import ModelHubEmptyState from './lib/ModelHubEmptyState';
import {PREDICTIONS_ILLUSTRATION} from './lib/modelHubIllustrations';
import {
  MODEL_HUB_LISTING_TABLE_VERTICAL_SPACING,
  useModelHubListingTableStyles,
} from './lib/modelHubListingTableStyles';
import ModelHubLoadingState from './lib/ModelHubLoadingState';
import ModelHubNoSearchResults from './lib/ModelHubNoSearchResults';
import SortableColumnHeader from './lib/SortableColumnHeader';
import TablePagination from './lib/TablePagination';
import {MODEL_HUB_MAIN_PADDING_Y_PX} from './ModelHubConstants';
import {InputChips} from './prediction-jobs/InputChips';
import type {PredictionJobRow} from './prediction-jobs/PredictionJobTypes';
import {
  readPredictionJobFiltersFromUrl,
  writePredictionJobFiltersToUrl,
} from './prediction-jobs/predictionJobUrlParams';
import type {PredictionJobSearchFilters} from './prediction-jobs/usePredictionJobSearch';
import {SUCCEEDED_ONLY, usePredictionJobSearch} from './prediction-jobs/usePredictionJobSearch';

type PredictionSortField = 'createdAt' | 'modifiedAt' | 'name';

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_STATE_DESCRIPTION = 'A prediction is an output generated by a model.';

interface PredictionJobResultsDownloadUrlQuery {
  predictionJob: {
    id: string;
    resultsDownloadUrl: string | null;
  } | null;
}

// Fire-and-forget: resolves the signed download URL and opens it in a new tab. Returns void so
// click handlers can call it directly without floating-promise / no-void lint violations.
function downloadPredictionResults(jobId: string): void {
  trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.DOWNLOAD_RESULTS_ZIP_CLICKED);
  doQuerySafe<PredictionJobResultsDownloadUrlQuery>(
    {query: PREDICTION_JOB_RESULTS_DOWNLOAD_URL_QUERY},
    {variables: {id: jobId}, fetchPolicy: 'no-cache'}
  )
    .then((result) => {
      const url = result.predictionJob?.resultsDownloadUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    })
    .catch(() => undefined);
}

function RowActionMenu({job}: {job: PredictionJobRow}): JSX.Element | null {
  const canDownloadResults = job.hasResultsFile;
  const canDownloadStructure = job.standardizedOutputDownloadUrl != null;

  if (!canDownloadResults && !canDownloadStructure) {
    return (
      <Text color="dimmed" size="sm">
        —
      </Text>
    );
  }

  return (
    <Menu position="bottom-end" withinPortal={true}>
      <Menu.Target>
        <ActionIcon
          aria-label={`Actions for ${job.id}`}
          onClick={(event) => {
            event.stopPropagation();
          }}
          size="sm"
          variant="ghost"
        >
          <BenchlingIcon icon="ellipsis" />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {canDownloadResults && (
          <Menu.Item
            icon={<BenchlingIcon icon="download" />}
            onClick={(event) => {
              event.stopPropagation();
              downloadPredictionResults(job.id);
            }}
          >
            Download all results (.zip)
          </Menu.Item>
        )}
        {canDownloadStructure && (
          <Menu.Item
            icon={<BenchlingIcon icon="download" />}
            onClick={(event) => {
              event.stopPropagation();
              trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.DOWNLOAD_STRUCTURE_PDB_OR_MMCIF_CLICKED);
              if (job.standardizedOutputDownloadUrl != null) {
                window.open(job.standardizedOutputDownloadUrl, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            Download structure (.cif)
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

interface PredictionsPageProps {
  catalogByExternalReference: ScientificModelsByExternalReference;
  models: ReadonlyArray<ScientificModelNode>;
  /** Omitted when signed out, which is what hides the feedback entry point. */
  onContactUs?: () => void;
  onNewPrediction: () => void;
  onSelectPrediction: (predictionId: string, name: string) => void;
  /** Hosts whose chrome already names the page (core's tabs) turn this off to avoid repeating it. */
  withTitle?: boolean;
}

export default function PredictionsPage({
  catalogByExternalReference,
  models,
  onContactUs,
  onNewPrediction,
  onSelectPrediction,
  withTitle = true,
}: PredictionsPageProps): JSX.Element {
  const {classes} = useStyles();
  const {classes: tableClasses} = useModelHubListingTableStyles();

  // The Predictions page lists only succeeded predictions; layer the status filter on top of URL state.
  const [filters, setFilters] = useState<PredictionJobSearchFilters>(() => ({
    ...readPredictionJobFiltersFromUrl(),
    statuses: SUCCEEDED_ONLY,
  }));
  const [searchInput, setSearchInput] = useState(filters.query);
  const [debouncedQuery] = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const [sortField, setSortField] = useState<PredictionSortField>('modifiedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const modelOptions = useMemo(
    () =>
      models.map((model) => ({
        value: model.externalReference,
        label: model.displayName,
      })),
    [models]
  );

  // Fold the debounced search box value into the filters object the search hook consumes.
  useEffect(() => {
    setFilters((prev) => (prev.query === debouncedQuery ? prev : {...prev, query: debouncedQuery}));
    if (debouncedQuery !== '') {
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTIONS_FILTERED, {filterType: 'query'});
    }
  }, [debouncedQuery]);

  // Reset to the first page whenever the result set changes.
  useEffect(() => {
    setOffset(0);
  }, [filters, sortField, sortDirection]);

  useEffect(() => {
    writePredictionJobFiltersToUrl(filters, {key: sortField, direction: sortDirection});
  }, [filters, sortField, sortDirection]);

  const sort = useMemo(() => ({key: sortField, direction: sortDirection}), [sortField, sortDirection]);
  // SortableColumnHeader expects a `field`-keyed shape; the search hook expects a `key`-keyed one.
  const sortState = useMemo(() => ({field: sortField, direction: sortDirection}), [sortField, sortDirection]);
  const {jobs, pagination, isLoading, error, refresh} = usePredictionJobSearch(
    filters,
    sort,
    offset,
    pageSize
  );
  // Wait for the persisted name rather than flashing the batch id from the URL.
  const batchFilterName = jobs[0]?.predictionBatchName || null;

  const handleSort = useCallback(
    (field: string) => {
      const typedField = field as PredictionSortField;
      if (typedField === sortField) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(typedField);
        setSortDirection('desc');
      }
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTIONS_SORTED, {sortField: typedField});
    },
    [sortField]
  );

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setOffset(0);
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTIONS_PAGINATION_CHANGED, {pageSize: size});
  }, []);

  const handleModelChange = useCallback((value: string | null) => {
    setFilters((prev) => ({...prev, modelName: value}));
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTIONS_FILTERED, {filterType: 'model'});
  }, []);

  const handleClearBatchFilter = useCallback(() => {
    setFilters((prev) => ({...prev, predictionBatchId: null}));
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTIONS_FILTERED, {filterType: 'predictionBatchId'});
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setFilters((prev) => ({...prev, query: '', modelName: null, predictionBatchId: null}));
  }, []);

  const handleOffsetChange = useCallback((nextOffset: number) => {
    setOffset(nextOffset);
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTIONS_PAGINATION_CHANGED, {offset: nextOffset});
  }, []);

  const renderContent = (): JSX.Element => {
    if (error) {
      return (
        <Alert
          color="red"
          icon={<BenchlingIcon icon="exclamation-circle" />}
          title="Failed to load predictions"
        >
          <Group spacing="xs">
            <Text size="sm">{error}</Text>
            <Text
              color="blue"
              component="button"
              onClick={refresh}
              size="sm"
              style={{background: 'none', border: 'none', cursor: 'pointer'}}
              td="underline"
            >
              Retry
            </Text>
          </Group>
        </Alert>
      );
    }

    if (isLoading && jobs.length === 0) {
      return (
        <ModelHubLoadingState testProps={getTestProps({component: 'PredictionsPage', element: 'loader'})} />
      );
    }

    if (jobs.length === 0) {
      const hasActiveFilters =
        filters.query !== '' || filters.modelName != null || filters.predictionBatchId != null;
      if (hasActiveFilters) {
        return <ModelHubNoSearchResults entity="predictions" onClearSearch={handleClearSearch} />;
      }
      return (
        <ModelHubEmptyState
          description={EMPTY_STATE_DESCRIPTION}
          illustration={PREDICTIONS_ILLUSTRATION}
          onNewPrediction={onNewPrediction}
          title="No predictions yet"
        />
      );
    }

    return (
      <Stack className={tableClasses.listingContent} spacing="lg">
        <Paper
          className={tableClasses.tablePaper}
          radius="md"
          withBorder={true}
          {...getTestProps({component: 'PredictionsPage', element: 'table'})}
        >
          <Table
            className={tableClasses.table}
            horizontalSpacing={14}
            verticalSpacing={MODEL_HUB_LISTING_TABLE_VERTICAL_SPACING}
          >
            <thead>
              <tr>
                <SortableColumnHeader field="name" label="Name" onSort={handleSort} sort={sortState} />
                <th>Inputs</th>
                <th>Batch</th>
                <th>Model</th>
                <SortableColumnHeader
                  field="createdAt"
                  label="Created"
                  onSort={handleSort}
                  sort={sortState}
                />
                <SortableColumnHeader
                  field="modifiedAt"
                  label="Last modified"
                  onSort={handleSort}
                  sort={sortState}
                />
                <th style={{width: 48}} />
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => {
                const model = requireScientificModel(catalogByExternalReference, job.modelName);
                return (
                  <tr
                    key={job.id}
                    {...getTestProps({component: 'PredictionsPage', element: 'row'})}
                    className={classes.clickableRow}
                    onClick={() => {
                      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTIONS_PREDICTION_OPENED);
                      onSelectPrediction(job.id, job.name);
                    }}
                  >
                    <td>
                      <Text
                        {...getTestProps({component: 'PredictionsPage', element: 'predictionName'})}
                        className={classes.nameCell}
                        size="lg"
                      >
                        {job.name}
                      </Text>
                    </td>
                    <td>
                      <InputChips configJson={job.configJson} />
                    </td>
                    <td>
                      <Text color="dimmed" size="md">
                        {job.predictionBatchName || '—'}
                      </Text>
                    </td>
                    <td>
                      <Text fw={600} size="lg">
                        {model.displayName}
                      </Text>
                    </td>
                    <td>
                      <Text color="dimmed" size="md">
                        {job.createdAt ? readableTime(job.createdAt) : '—'}
                      </Text>
                    </td>
                    <td>
                      <Text color="dimmed" size="md">
                        {job.modifiedAt ? readableTime(job.modifiedAt) : '—'}
                      </Text>
                    </td>
                    <td
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <RowActionMenu job={job} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Paper>
        <TablePagination
          onOffsetChange={handleOffsetChange}
          onPageSizeChange={handlePageSizeChange}
          pagination={pagination}
        />
      </Stack>
    );
  };

  return (
    <Stack
      className={classes.content}
      spacing="lg"
      {...getTestProps({component: 'PredictionsPage', element: 'root'})}
    >
      <Stack spacing="md">
        {(withTitle || onContactUs != null) && (
          <Group noWrap={true} pb={MODEL_HUB_MAIN_PADDING_Y_PX} position="apart">
            {withTitle && <Title order={1}>Predictions</Title>}
            {onContactUs != null && <GetInTouchButton onClick={onContactUs} />}
          </Group>
        )}

        <Group noWrap={true} position="apart" spacing="sm">
          <TextInput
            aria-label="Search predictions"
            className={classes.searchInput}
            icon={<BenchlingIcon icon="search" />}
            onChange={(event) => {
              setSearchInput(event.currentTarget.value);
            }}
            placeholder="Search predictions"
            size="lg"
            value={searchInput}
          />
          <Group spacing="sm">
            {filters.predictionBatchId != null && batchFilterName != null && (
              <Badge
                color="gray"
                rightSection={
                  <ActionIcon
                    aria-label="Clear batch filter"
                    color="gray"
                    onClick={handleClearBatchFilter}
                    radius="xl"
                    size="xs"
                    variant="transparent"
                  >
                    <BenchlingIcon icon="close" />
                  </ActionIcon>
                }
                size="lg"
                tt="none"
                variant="light"
              >
                Batch: {batchFilterName}
              </Badge>
            )}
            <Select
              aria-label="Filter by model"
              className={classes.modelSelect}
              clearable={true}
              data={modelOptions}
              icon={<BenchlingIcon icon="lightbulb" />}
              maxDropdownHeight={400}
              onChange={handleModelChange}
              placeholder="All models"
              searchable={true}
              size="lg"
              value={filters.modelName}
            />
          </Group>
        </Group>
      </Stack>

      {renderContent()}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  clickableRow: {
    cursor: 'pointer',
  },
  content: {
    height: '100%',
    minHeight: 0,
  },
  modelSelect: {
    flexShrink: 0,
    width: 240,
  },
  nameCell: {
    fontFamily: theme.fontFamilyMonospace,
    fontWeight: 600,
  },
  searchInput: {
    flex: 1,
    maxWidth: 480,
    width: '100%',
  },
}));
